import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container } from "react-bootstrap";
import { searchIcon } from "../assets";
import {
  FaTimes,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaEllipsisH,
} from "react-icons/fa";
import scheduleIcon from "../assets/images/Formicons/shedule.svg";
import timeIcon from "../assets/images/Formicons/time_duration.svg";
import addressIcon from "../assets/images/Formicons/address_icon.svg";
import offerIcon from "../assets/images/Formicons/offer_name.svg";
import addFlashSaleIcon from "../assets/images/Formicons/add_flash_sale.svg";
import deleteConfirmIcon from "../assets/images/Formicons/delet_confirm.svg";
import pauseConfirmIcon from "../assets/images/Formicons/pause_confirm.svg";
import discountFlashIcon from "../assets/images/Formicons/discount_flash.svg";
import {
  getFlashSales,
  createFlashSale,
  updateFlashSale,
  deleteFlashSale,
  getEnabledServices,
} from "../features/flashSales/flashSalesSlice";
import "../css/FlashSales.css";
import { ShimmerFlashSales } from "../components/Shimmer";

// ==================== DATE / TIME HELPERS ====================

// Native date input already gives "YYYY-MM-DD" — pass through directly
const parseDateToISO = (dateStr) => dateStr || "";

// "28/02/2026" → "2026-02-28"  (used when prefilling from API response)
const displayDateToInputDate = (dateStr) => {
  if (!dateStr) return "";
  const [day, month, year] = dateStr.split("/");
  return `${year}-${month}-${day}`;
};

// "2026-02-28" → "28 Feb 2026"  (human-friendly label shown in the box)
const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const [year, month, day] = isoDate.split("-");
  return `${day} ${monthNames[parseInt(month, 10) - 1]} ${year}`;
};

// "07:00 AM" → "07:00"
const parseTimeTo24 = (timeStr) => {
  if (!timeStr) return "";
  const [time, period] = timeStr.split(" ");
  let [hour, minute] = time.split(":").map(Number);
  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

// "07:00 AM" → { hour: "07", minute: "00", period: "AM" }
const parseTimeDisplay = (timeStr) => {
  if (!timeStr) return { hour: "07", minute: "00", period: "AM" };
  const parts = timeStr.trim().split(" ");
  const period = parts[1] || "AM";
  const [hour, minute] = parts[0].split(":");
  return {
    hour: hour.padStart(2, "0"),
    minute: minute.padStart(2, "0"),
    period,
  };
};

// "ACTIVE" → "Active"
const formatStatus = (status) => {
  if (!status) return "";
  if (status === "INACTIVE") return "Paused";
  return status.charAt(0) + status.slice(1).toLowerCase();
};

const getStatusClass = (status) => {
  if (status === "ACTIVE") return "active";
  if (status === "PAUSED" || status === "INACTIVE") return "paused";
  if (status === "EXPIRED") return "expired";
  return "";
};

// ==================== SERVICE TOOLTIP ====================
const ServiceTooltip = ({ services, summary }) => {
  const [show, setShow] = useState(false);
  return (
    <div
      className="flash-sales-service-tooltip-wrapper"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span className="flash-sales-service-link">{summary}</span>
      {show && services?.length > 0 && (
        <div className="flash-sales-service-tooltip">
          {services.map((s, i) => (
            <div key={i} className="flash-sales-service-tooltip-item">
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== THREE DOT MENU ====================
const ThreeDotMenu = ({ item, tab, onPause, onDelete, onRestart, onEdit }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isPaused = item.Status === "PAUSED" || item.Status === "INACTIVE";

  return (
    <div className="flash-sales-dots-wrapper" ref={ref}>
      <button className="flash-sales-dots-btn" onClick={() => setOpen(!open)}>
        <FaEllipsisH size={14} />
      </button>
      {open && (
        <div className="flash-sales-dots-menu">
          {tab === "Ongoing" ? (
            <>
              <button onClick={() => { setOpen(false); onEdit(item); }}>Edit</button>
              <button onClick={() => { setOpen(false); onPause(item); }}>
                {isPaused ? "Unpause" : "Pause"}
              </button>
              <button className="danger" onClick={() => { setOpen(false); onDelete(item); }}>
                Delete
              </button>
            </>
          ) : (
            <>
              <button onClick={() => { setOpen(false); onRestart(item); }}>Restart</button>
              <button className="danger" onClick={() => { setOpen(false); onDelete(item); }}>
                Delete
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== CONFIRM MODAL ====================
const ConfirmModal = ({ type, onConfirm, onCancel, isLoading }) => {
  const isDestructive = type === "delete";
  const isPause = type === "pause";
  const isResume = type === "resume";

  const message = isPause
    ? "Are you sure you want to pause this sale?"
    : isResume
      ? "Are you sure you want to resume this sale?"
      : "Are you sure you want to delete this sale?";

  const btnClass = isDestructive
    ? "flash-sales-confirm-btn danger"
    : "flash-sales-confirm-btn primary";
  const btnLabel = isPause ? "PAUSE" : isResume ? "RESUME" : "DELETE";

  return (
    <div className="flash-sales-modal-overlay" onClick={onCancel}>
      <div className="flash-sales-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="flash-sales-confirm-header">
          <button className="flash-sales-confirm-close" onClick={onCancel}>
            <FaTimes size={14} />
          </button>
        </div>
        <div className="flash-sales-confirm-body">
          <div className="flash-sales-confirm-icon">
            <img
              src={isDestructive ? deleteConfirmIcon : pauseConfirmIcon}
              alt=""
              style={{ width: 80, height: 80 }}
            />
          </div>
          <p className="flash-sales-confirm-message">{message}</p>
        </div>
        <div className="flash-sales-confirm-footer">
          <button className="flash-sales-confirm-btn outline" onClick={onCancel} disabled={isLoading}>
            CANCEL
          </button>
          <button className={btnClass} onClick={onConfirm} disabled={isLoading}>
            {isLoading ? "..." : btnLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const FlashSales = () => {
  const dispatch = useDispatch();
  const {
    sales,
    tabCounts,
    serviceOptions,
    isLoading,
    isCreating,
    isUpdating,
    isLoadingServices,
  } = useSelector((state) => state.flashSales);

  const [selectedTab, setSelectedTab] = useState("Ongoing");
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  // "create" | "edit" | "restart"
  const [modalMode, setModalMode] = useState("create");
  const [editItem, setEditItem] = useState(null);
  const [discountType, setDiscountType] = useState("AMOUNT");
  const [confirmState, setConfirmState] = useState(null);
  const [createResult, setCreateResult] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    offerName: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    selectedSubServices: {},
    discountPrice: "",
    maxDiscount: "",
  });

  const [expandedServices, setExpandedServices] = useState({});
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  const [startHour, setStartHour] = useState("07");
  const [startMinute, setStartMinute] = useState("00");
  const [startPeriod, setStartPeriod] = useState("AM");
  const [endHour, setEndHour] = useState("07");
  const [endMinute, setEndMinute] = useState("00");
  const [endPeriod, setEndPeriod] = useState("PM");

  // Hidden date input refs — programmatically open the browser calendar picker
  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);

  const startTimeRef = useRef(null);
  const endTimeRef = useRef(null);
  const serviceDropdownRef = useRef(null);

  // ==================== INITIAL FETCH ====================
  useEffect(() => {
    dispatch(getEnabledServices());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getFlashSales({ tab: selectedTab.toLowerCase() }));
  }, [dispatch, selectedTab]);

  // ==================== SEARCH DEBOUNCE ====================
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getFlashSales({ tab: selectedTab.toLowerCase(), search: searchQuery }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, dispatch, selectedTab]);

  // ==================== CLICK OUTSIDE ====================
  useEffect(() => {
    const handler = (e) => {
      if (startTimeRef.current && !startTimeRef.current.contains(e.target))
        setShowStartTimePicker(false);
      if (endTimeRef.current && !endTimeRef.current.contains(e.target))
        setShowEndTimePicker(false);
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(e.target))
        setShowServiceDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ==================== PREFILL FORM FROM ITEM ====================
  const prefillFormFromItem = useCallback((item) => {
    const selectedSubServices = {};
    (item.AffectedServices || []).forEach((service) => {
      selectedSubServices[service.ServiceId] = service.SubServices.map(
        (s) => s.SubServiceId,
      );
    });

    const st = parseTimeDisplay(item.FromTime);
    const et = parseTimeDisplay(item.ToTime);

    setFormData({
      offerName: item.Name || "",
      // API returns "28/02/2026" → convert to "2026-02-28" for input[type="date"]
      startDate: displayDateToInputDate(item.From),
      startTime: item.FromTime || "",
      endDate: displayDateToInputDate(item.To),
      endTime: item.ToTime || "",
      selectedSubServices,
      discountPrice: item.DiscountValue || "",
      maxDiscount: item.MaxDiscount || "",
    });
    setDiscountType(item.DiscountType || "AMOUNT");
    setStartHour(st.hour);
    setStartMinute(st.minute);
    setStartPeriod(st.period);
    setEndHour(et.hour);
    setEndMinute(et.minute);
    setEndPeriod(et.period);
  }, []);

  // ==================== BUILD PAYLOAD ====================
  const buildPayload = useCallback(
    (mode) => {
      const subServiceIds = Object.values(formData.selectedSubServices).flat();
      const payload = {
        Name: formData.offerName,
        SubServiceIds: subServiceIds,
        StartDate: parseDateToISO(formData.startDate),
        StartTime: parseTimeTo24(formData.startTime),
        EndDate: parseDateToISO(formData.endDate),
        EndTime: parseTimeTo24(formData.endTime),
        DiscountType: discountType,
        DiscountValue: parseFloat(formData.discountPrice),
      };
      if (discountType === "PERCENTAGE" && formData.maxDiscount) {
        payload.MaxDiscount = parseFloat(formData.maxDiscount);
      }
      if (mode === "edit" || mode === "restart") {
        payload.Action = mode;
      }
      return payload;
    },
    [formData, discountType],
  );

  // ==================== 3-DOT HANDLERS ====================
  const handlePauseClick = (item) => {
    setConfirmState({
      type: item.Status === "PAUSED" || item.Status === "INACTIVE" ? "resume" : "pause",
      item,
    });
  };

  const handleDeleteClick = (item) => {
    setConfirmState({ type: "delete", item });
  };

  const handleRestartClick = (item) => {
    setEditItem(item);
    setModalMode("restart");
    prefillFormFromItem(item);
    setFormData((prev) => ({
      ...prev,
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
    }));
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const handleEditClick = (item) => {
    setEditItem(item);
    setModalMode("edit");
    prefillFormFromItem(item);
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const handleConfirmAction = async () => {
    if (!confirmState) return;
    const { type, item } = confirmState;

    if (type === "delete") {
      const result = await dispatch(deleteFlashSale(item.FlashId));
      if (deleteFlashSale.fulfilled.match(result)) {
        setConfirmState(null);
        dispatch(getFlashSales({ tab: selectedTab.toLowerCase(), search: searchQuery }));
      }
    } else {
      const action = type === "pause" ? "pause" : "resume";
      const result = await dispatch(
        updateFlashSale({ flashId: item.FlashId, data: { Action: action } }),
      );
      if (updateFlashSale.fulfilled.match(result)) {
        setConfirmState(null);
        dispatch(getFlashSales({ tab: selectedTab.toLowerCase(), search: searchQuery }));
      }
    }
  };

  // ==================== SUBMIT SALE ====================
  const handleAddSale = async () => {
    if (modalMode === "create") {
      const payload = buildPayload("create");
      const result = await dispatch(createFlashSale(payload));
      if (createFlashSale.fulfilled.match(result)) {
        handleCloseModal();
        setCreateResult(result.payload);
        dispatch(getFlashSales({ tab: "ongoing" }));
        setSelectedTab("Ongoing");
      }
    } else if (modalMode === "edit") {
      const payload = buildPayload("edit");
      const result = await dispatch(
        updateFlashSale({ flashId: editItem.FlashId, data: payload }),
      );
      if (updateFlashSale.fulfilled.match(result)) {
        handleCloseModal();
        dispatch(getFlashSales({ tab: selectedTab.toLowerCase(), search: searchQuery }));
      }
    } else if (modalMode === "restart") {
      const payload = buildPayload("restart");
      const result = await dispatch(
        updateFlashSale({ flashId: editItem.FlashId, data: payload }),
      );
      if (updateFlashSale.fulfilled.match(result)) {
        handleCloseModal();
        dispatch(getFlashSales({ tab: "ongoing" }));
        setSelectedTab("Ongoing");
      }
    }
  };

  // ==================== MODAL HELPERS ====================
  const resetForm = () => {
    setFormData({
      offerName: "",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      selectedSubServices: {},
      discountPrice: "",
      maxDiscount: "",
    });
    setDiscountType("AMOUNT");
    setExpandedServices({});
    setShowStartTimePicker(false);
    setShowEndTimePicker(false);
    setShowServiceDropdown(false);
    setEditItem(null);
    setModalMode("create");
  };

  const handleOpenModal = () => {
    setModalMode("create");
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const handleCloseModal = () => {
    setShowModal(false);
    document.body.style.overflow = "unset";
    resetForm();
  };

  // ==================== TIME HANDLERS ====================
  const handleStartTimeConfirm = () => {
    setFormData((prev) => ({
      ...prev,
      startTime: `${startHour}:${startMinute} ${startPeriod}`,
    }));
    setShowStartTimePicker(false);
  };

  const handleEndTimeConfirm = () => {
    setFormData((prev) => ({
      ...prev,
      endTime: `${endHour}:${endMinute} ${endPeriod}`,
    }));
    setShowEndTimePicker(false);
  };

  // ==================== SERVICE ACCORDION ====================
  const toggleServiceExpand = (serviceId) => {
    setExpandedServices((prev) => ({ ...prev, [serviceId]: !prev[serviceId] }));
  };

  const getAllSubIds = (service) => service.SubServices.map((s) => s.SubServiceId);

  const isParentChecked = (service) => {
    const selected = formData.selectedSubServices[service.ServiceId] || [];
    return selected.length === service.SubServices.length;
  };

  const isParentIndeterminate = (service) => {
    const selected = formData.selectedSubServices[service.ServiceId] || [];
    return selected.length > 0 && selected.length < service.SubServices.length;
  };

  const isSubChecked = (serviceId, subServiceId) => {
    return (formData.selectedSubServices[serviceId] || []).includes(subServiceId);
  };

  const handleSubServiceToggle = (serviceId, subServiceId) => {
    setFormData((prev) => {
      const current = prev.selectedSubServices[serviceId] || [];
      const exists = current.includes(subServiceId);
      const updated = exists
        ? current.filter((id) => id !== subServiceId)
        : [...current, subServiceId];
      return {
        ...prev,
        selectedSubServices: { ...prev.selectedSubServices, [serviceId]: updated },
      };
    });
  };

  const handleParentToggle = (service) => {
    const allSubs = getAllSubIds(service);
    const currentlyAllSelected = isParentChecked(service);
    setFormData((prev) => ({
      ...prev,
      selectedSubServices: {
        ...prev.selectedSubServices,
        [service.ServiceId]: currentlyAllSelected ? [] : allSubs,
      },
    }));
  };

  const getServiceSummaryLabel = () => {
    const parts = [];
    serviceOptions.forEach((service) => {
      const selected = formData.selectedSubServices[service.ServiceId] || [];
      if (selected.length > 0) parts.push(`${service.ServiceName} (${selected.length})`);
    });
    return parts.length > 0 ? parts.join(", ") : null;
  };

  const totalSelectedCount = Object.values(formData.selectedSubServices).reduce(
    (sum, arr) => sum + arr.length,
    0,
  );

  const isFormValid =
    formData.offerName.trim() &&
    formData.startDate &&
    formData.startTime &&
    formData.endDate &&
    formData.endTime &&
    totalSelectedCount > 0 &&
    formData.discountPrice;

  const modalTitle =
    modalMode === "edit"
      ? "Edit Flash Sale"
      : modalMode === "restart"
        ? "Restart Flash Sale"
        : "Add Flash Sale";

  const submitLabel =
    modalMode === "edit"
      ? "SAVE CHANGES"
      : modalMode === "restart"
        ? "RESTART SALE"
        : "ADD SALE";

  const isSubmitting = isCreating || isUpdating;

  // Today in YYYY-MM-DD for min attribute
  const todayStr = new Date().toISOString().split("T")[0];

  // ==================== RENDER TIME PICKER ====================
  const renderTimePicker = (hour, setHour, minute, setMinute, period, setPeriod, onConfirm) => (
    <div className="flash-sales-time-picker">
      <div className="flash-sales-time-inputs">
        <div className="flash-sales-time-group">
          <label>Hour</label>
          <input
            type="number"
            value={hour}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              if (v >= 1 && v <= 12) setHour(String(v).padStart(2, "0"));
            }}
            min="1"
            max="12"
          />
        </div>
        <span className="flash-sales-time-separator">:</span>
        <div className="flash-sales-time-group">
          <label>Minute</label>
          <input
            type="number"
            value={minute}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              if (v >= 0 && v <= 59) setMinute(String(v).padStart(2, "0"));
            }}
            min="0"
            max="59"
          />
        </div>
        <select
          className="flash-sales-period-select"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
      <button type="button" className="flash-sales-time-confirm" onClick={onConfirm}>
        Set Time
      </button>
    </div>
  );

  // ==================== RENDER SERVICE ACCORDION ====================
  const renderServiceAccordion = () => {
    if (isLoadingServices) {
      return (
        <div style={{ padding: "16px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
          Loading services...
        </div>
      );
    }
    if (serviceOptions.length === 0) {
      return (
        <div style={{ padding: "16px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
          No services enabled
        </div>
      );
    }
    return (
      <div className="flash-sales-service-accordion">
        {serviceOptions.map((service) => {
          const isExpanded = !!expandedServices[service.ServiceId];
          const parentChecked = isParentChecked(service);
          const parentIndet = isParentIndeterminate(service);
          return (
            <div key={service.ServiceId} className="flash-sales-service-item">
              <div className="flash-sales-service-parent">
                <span
                  className={`flash-sales-accord-checkbox ${parentChecked ? "checked" : ""} ${parentIndet ? "indeterminate" : ""}`}
                  onClick={(e) => { e.stopPropagation(); handleParentToggle(service); }}
                >
                  {parentChecked && <FaCheck size={9} />}
                  {parentIndet && <span className="flash-sales-indeterminate-dash" />}
                </span>
                <span
                  className="flash-sales-service-name"
                  onClick={() => toggleServiceExpand(service.ServiceId)}
                >
                  {service.ServiceName}
                </span>
                <span
                  className="flash-sales-service-arrow"
                  onClick={() => toggleServiceExpand(service.ServiceId)}
                >
                  {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                </span>
              </div>
              {isExpanded && (
                <div className="flash-sales-subservices">
                  {service.SubServices.map((sub) => {
                    const subChecked = isSubChecked(service.ServiceId, sub.SubServiceId);
                    return (
                      <label
                        key={sub.SubServiceId}
                        className={`flash-sales-subservice-row ${subChecked ? "checked" : ""}`}
                        onClick={() => handleSubServiceToggle(service.ServiceId, sub.SubServiceId)}
                      >
                        <span className={`flash-sales-accord-checkbox ${subChecked ? "checked" : ""}`}>
                          {subChecked && <FaCheck size={9} />}
                        </span>
                        <span className="flash-sales-subservice-name">
                          {sub.SubServiceName}
                          {sub.EffectivePrice && (
                            <span style={{ color: "#94a3b8", marginLeft: 6, fontSize: 12 }}>
                              {sub.Currency}{sub.EffectivePrice}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ==================== MAIN RENDER ====================
  return (
    <div className="flash-sales-page">
      <Container fluid className="px-4 py-4">
        {/* Controls Row */}
        <div className="flash-sales-controls">
          <div className="flash-sales-tabs">
            <button
              className={`flash-sales-tab ${selectedTab === "Ongoing" ? "active" : ""}`}
              onClick={() => setSelectedTab("Ongoing")}
            >
              Ongoing Sales
              {tabCounts.Ongoing > 0 && (
                <span className="flash-sales-tab-count">{tabCounts.Ongoing}</span>
              )}
            </button>
            <button
              className={`flash-sales-tab ${selectedTab === "Past" ? "active" : ""}`}
              onClick={() => setSelectedTab("Past")}
            >
              Past Sales
              {tabCounts.Past > 0 && (
                <span className="flash-sales-tab-count">{tabCounts.Past}</span>
              )}
            </button>
          </div>

          <div className="flash-sales-actions">
            <div className="flash-sales-search">
              <input
                type="text"
                placeholder="Search by offer title"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flash-sales-search-input"
              />
              <img src={searchIcon} alt="Search" className="flash-sales-search-icon" />
            </div>
            <button className="flash-sales-create-btn" onClick={handleOpenModal}>
              CREATE SALE +
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flash-sales-table-container">
          <table className="flash-sales-table">
            <thead>
              <tr>
                <th>Offer</th>
                <th>From</th>
                <th>To</th>
                <th>Service</th>
                <th>Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} style={{ padding: 0 }}>
                    <ShimmerFlashSales rows={5} />
                  </td>
                </tr>
              ) : sales.length === 0 ? (
                <tr>
                  <td colSpan={7} className="flash-sales-empty">
                    No flash sales found
                  </td>
                </tr>
              ) : (
                sales.map((item) => (
                  <tr key={item.FlashId}>
                    <td className="flash-sales-offer-name" data-label="Offer">{item.Name}</td>
                    <td data-label="From">
                      <div className="flash-sales-date-cell">
                        <span className="flash-sales-date">{item.From}</span>
                        <span className="flash-sales-time">{item.FromTime}</span>
                      </div>
                    </td>
                    <td data-label="To">
                      <div className="flash-sales-date-cell">
                        <span className="flash-sales-date">{item.To}</span>
                        <span className="flash-sales-time">{item.ToTime}</span>
                      </div>
                    </td>
                    <td data-label="Service">
                      <ServiceTooltip services={item.ServiceTooltip} summary={item.ServiceSummary} />
                    </td>
                    <td data-label="Amount">
                      <div className="flash-sales-amount-cell">
                        {(() => {
                          const [value, ...labelParts] = (item.AmountDisplay || "").split(" ");
                          const label = labelParts.join(" ");
                          return (
                            <>
                              <span className="flash-sales-amount-value">{value}</span>
                              {label && <span className="flash-sales-amount-label">{label}</span>}
                            </>
                          );
                        })()}
                      </div>
                    </td>
                    <td data-label="Status">
                      <span className={`flash-sales-status ${getStatusClass(item.Status)}`}>
                        {formatStatus(item.Status)}
                      </span>
                    </td>
                    <td>
                      <ThreeDotMenu
                        item={item}
                        tab={selectedTab}
                        onPause={handlePauseClick}
                        onDelete={handleDeleteClick}
                        onRestart={handleRestartClick}
                        onEdit={handleEditClick}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Container>

      {/* ==================== CREATE / EDIT / RESTART MODAL ==================== */}
      {showModal && (
        <div className="flash-sales-modal-overlay" onClick={handleCloseModal}>
          <div className="flash-sales-modal" onClick={(e) => e.stopPropagation()}>
            <div className="flash-sales-modal-header">
              <div className="flash-sales-modal-title">
                <div className="flash-sales-modal-icon">
                  <img src={addFlashSaleIcon} alt="" className="flash-sales-modal-header-icon" />
                </div>
                <h3>{modalTitle}</h3>
              </div>
              <button className="flash-sales-modal-close" onClick={handleCloseModal}>
                <FaTimes size={18} />
              </button>
            </div>

            <div className="flash-sales-modal-body">
              {/* Offer Name */}
              <div className="flash-sales-form-group">
                <label>Offer Name</label>
                <div className="flash-sales-input-wrapper">
                  <img src={offerIcon} alt="" className="flash-sales-input-icon" />
                  <input
                    type="text"
                    value={formData.offerName}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, offerName: e.target.value }))
                    }
                    placeholder="Enter offer name"
                  />
                </div>
              </div>

              {/* Start Date & Time */}
              <div className="flash-sales-form-row">

                {/* START DATE
                    Strategy: render the existing styled .flash-sales-input-field box.
                    Clicking anywhere on it calls showPicker() on a hidden
                    input[type="date"] — so the whole row is the click target,
                    and the user can never type manually (input is invisible). */}
                <div className="flash-sales-form-group">
                  <label>Start Date</label>
                  <div
                    className="flash-sales-input-field"
                    style={{ cursor: "pointer" }}
                    onClick={() => startDateInputRef.current?.showPicker()}
                  >
                    <img src={scheduleIcon} alt="" className="flash-sales-field-icon" />
                    <span
                      className={
                        formData.startDate
                          ? "flash-sales-field-value"
                          : "flash-sales-field-placeholder"
                      }
                    >
                      {formData.startDate
                        ? formatDisplayDate(formData.startDate)
                        : "Starting from"}
                    </span>
                    {/* Hidden native date input — only used to open the browser picker */}
                    <input
                      ref={startDateInputRef}
                      type="date"
                      value={formData.startDate}
                      min={todayStr}
                      onChange={(e) => {
                        const newStart = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          startDate: newStart,
                          // auto-clear end date if it falls before new start date
                          endDate:
                            prev.endDate && prev.endDate < newStart ? "" : prev.endDate,
                        }));
                      }}
                      style={{
                        position: "absolute",
                        opacity: 0,
                        pointerEvents: "none",
                        width: 0,
                        height: 0,
                        border: "none",
                        padding: 0,
                      }}
                      tabIndex={-1}
                    />
                  </div>
                </div>

                {/* START TIME */}
                <div className="flash-sales-form-group" ref={startTimeRef}>
                  <label>Start Time</label>
                  <div
                    className="flash-sales-input-field"
                    onClick={() => {
                      setShowStartTimePicker(!showStartTimePicker);
                      setShowEndTimePicker(false);
                    }}
                  >
                    <img src={timeIcon} alt="" className="flash-sales-field-icon" />
                    <span
                      className={
                        formData.startTime
                          ? "flash-sales-field-value"
                          : "flash-sales-field-placeholder"
                      }
                    >
                      {formData.startTime || "Start time"}
                    </span>
                  </div>
                  {showStartTimePicker &&
                    renderTimePicker(
                      startHour, setStartHour,
                      startMinute, setStartMinute,
                      startPeriod, setStartPeriod,
                      handleStartTimeConfirm,
                    )}
                </div>
              </div>

              {/* End Date & Time */}
              <div className="flash-sales-form-row">

                {/* END DATE — same hidden-input pattern as Start Date */}
                <div className="flash-sales-form-group">
                  <label>End Date</label>
                  <div
                    className="flash-sales-input-field"
                    style={{ cursor: "pointer" }}
                    onClick={() => endDateInputRef.current?.showPicker()}
                  >
                    <img src={scheduleIcon} alt="" className="flash-sales-field-icon" />
                    <span
                      className={
                        formData.endDate
                          ? "flash-sales-field-value"
                          : "flash-sales-field-placeholder"
                      }
                    >
                      {formData.endDate
                        ? formatDisplayDate(formData.endDate)
                        : "Ending On"}
                    </span>
                    {/* Hidden native date input — only used to open the browser picker */}
                    <input
                      ref={endDateInputRef}
                      type="date"
                      value={formData.endDate}
                      min={formData.startDate || todayStr}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, endDate: e.target.value }))
                      }
                      style={{
                        position: "absolute",
                        opacity: 0,
                        pointerEvents: "none",
                        width: 0,
                        height: 0,
                        border: "none",
                        padding: 0,
                      }}
                      tabIndex={-1}
                    />
                  </div>
                </div>

                {/* END TIME */}
                <div className="flash-sales-form-group" ref={endTimeRef}>
                  <label>End Time</label>
                  <div
                    className="flash-sales-input-field"
                    onClick={() => {
                      setShowEndTimePicker(!showEndTimePicker);
                      setShowStartTimePicker(false);
                    }}
                  >
                    <img src={timeIcon} alt="" className="flash-sales-field-icon" />
                    <span
                      className={
                        formData.endTime
                          ? "flash-sales-field-value"
                          : "flash-sales-field-placeholder"
                      }
                    >
                      {formData.endTime || "End time"}
                    </span>
                  </div>
                  {showEndTimePicker &&
                    renderTimePicker(
                      endHour, setEndHour,
                      endMinute, setEndMinute,
                      endPeriod, setEndPeriod,
                      handleEndTimeConfirm,
                    )}
                </div>
              </div>

              {/* Service Type */}
              <div className="flash-sales-form-group" ref={serviceDropdownRef}>
                <label>Service Type</label>
                <div className="flash-sales-accord-wrapper">
                  <div
                    className={`flash-sales-accord-trigger ${showServiceDropdown ? "open" : ""}`}
                    onClick={() => setShowServiceDropdown(!showServiceDropdown)}
                  >
                    <img src={addressIcon} alt="" className="flash-sales-field-icon" />
                    <span
                      className={
                        totalSelectedCount > 0
                          ? "flash-sales-field-value"
                          : "flash-sales-field-placeholder"
                      }
                    >
                      {getServiceSummaryLabel() || "Apply to"}
                    </span>
                    <FaChevronDown
                      size={12}
                      className={`flash-sales-chevron ${showServiceDropdown ? "open" : ""}`}
                    />
                  </div>
                  {showServiceDropdown && (
                    <div className="flash-sales-accord-panel">
                      {renderServiceAccordion()}
                    </div>
                  )}
                </div>
              </div>

              {/* Discount Price */}
              <div className="flash-sales-form-group">
                <label>Discount Price</label>
                <div className="flash-sales-discount-wrapper">
                  <div className="flash-sales-input-wrapper flash-sales-discount-input">
                    <img src={discountFlashIcon} alt="" className="flash-sales-input-icon" />
                    <input
                      type="number"
                      value={formData.discountPrice}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, discountPrice: e.target.value }))
                      }
                      placeholder={discountType === "AMOUNT" ? "Discount Price" : "Discount %"}
                      min="0"
                      step={discountType === "PERCENTAGE" ? "1" : "0.01"}
                      max={discountType === "PERCENTAGE" ? "100" : undefined}
                    />
                  </div>
                  <div className="flash-sales-discount-toggle">
                    <button
                      type="button"
                      className={`flash-sales-discount-btn ${discountType === "AMOUNT" ? "active" : ""}`}
                      onClick={() => setDiscountType("AMOUNT")}
                    >
                      Amount
                    </button>
                    <button
                      type="button"
                      className={`flash-sales-discount-btn ${discountType === "PERCENTAGE" ? "active" : ""}`}
                      onClick={() => setDiscountType("PERCENTAGE")}
                    >
                      Percentage
                    </button>
                  </div>
                </div>
              </div>

              {/* Max Discount Cap — only for PERCENTAGE */}
              {discountType === "PERCENTAGE" && (
                <div className="flash-sales-form-group">
                  <label>
                    Max Discount Cap{" "}
                    <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: 14 }}>
                      (optional)
                    </span>
                  </label>
                  <div className="flash-sales-input-wrapper">
                    <span
                      className="flash-sales-input-icon"
                      style={{ fontSize: 16, fontWeight: 600, color: "#94a3b8" }}
                    >
                      $
                    </span>
                    <input
                      type="number"
                      value={formData.maxDiscount}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, maxDiscount: e.target.value }))
                      }
                      placeholder="e.g. 12 → 15% Up to $12"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flash-sales-modal-footer">
              <button
                className="flash-sales-add-btn"
                onClick={handleAddSale}
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? "Saving..." : submitLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== CREATE RESULT MODAL ==================== */}
      {createResult && (
        <div className="flash-sales-modal-overlay" onClick={() => setCreateResult(null)}>
          <div className="flash-sales-result-modal" onClick={(e) => e.stopPropagation()}>
            <div className="flash-sales-modal-header">
              <div className="flash-sales-modal-title">
                <h3>Sale Created</h3>
              </div>
              <button className="flash-sales-modal-close" onClick={() => setCreateResult(null)}>
                <FaTimes size={18} />
              </button>
            </div>
            <div className="flash-sales-result-body">
              {createResult.FlashSale && (
                <div className="flash-sales-result-section success">
                  <div className="flash-sales-result-section-header">
                    <FaCheck size={14} />
                    <span>{createResult.Message || "Flash sale created successfully"}</span>
                  </div>
                  {(createResult.FlashSale.AffectedServices || []).map((service, idx) => (
                    <div key={idx} className="flash-sales-result-item">
                      <span className="flash-sales-result-name">{service.ServiceName}</span>
                      <span className="flash-sales-result-meta">
                        {createResult.FlashSale.AmountDisplay} ·{" "}
                        {service.SubServices?.length || 0} variant
                        {service.SubServices?.length !== 1 ? "s" : ""} affected
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flash-sales-modal-footer">
              <button className="flash-sales-add-btn" onClick={() => setCreateResult(null)}>
                DONE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== CONFIRM MODAL ==================== */}
      {confirmState && (
        <ConfirmModal
          type={confirmState.type}
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmState(null)}
          isLoading={isUpdating}
        />
      )}
    </div>
  );
};

export default FlashSales;